/**
 * Sign-in, registration, email verification, password recovery and the Account screen's
 * calls (profile, onboarding, password change, API keys).
 *
 * Part of the Console's API client: import from `engine/api.js`, which re-exports this module.
 */

import {
  BASE,
  headers,
  setToken,
  clearToken,
  signalPasswordChanged,
  parseJsonOrThrow,
  fetchJson,
  fetchNoContent,
} from "./apiTransport";

/**
 * POST /auth/login - authenticate and receive a JWT.
 *
 * Thin transport ONLY: this returns the TokenResponse and deliberately does NOT
 * persist the JWT. Token persistence is the CALLER's responsibility (useAuth.
 * doLogin) so that out-of-order completion of overlapping login attempts cannot
 * cross sessions — only the winning (latest) attempt commits its token via
 * setToken. If api.login stored the token itself, a superseded attempt resolving
 * LAST would overwrite a newer session's credential (tenant/account crossover).
 * The winning-generation guard lives in useAuth (the auth-state owner).
 */
export async function login(identifier, password) {
  const res = await fetch(`${BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // OMEGA-814 — the login identity is the customer's email. Sent as `identifier`
    // rather than `email` because that is what the field IS: for an account created
    // before this change there is no stored email, and the customer signs in with the
    // username they have always used. The backend collects every row the value could
    // name and lets the password pick between them, so both reach the right row through
    // this one key. (It does NOT try email first: the two namespaces overlap, and
    // preferring either one locks the other account out.)
    body: JSON.stringify({ identifier, password }),
  });
  return parseJsonOrThrow(res);
}

/**
 * POST /auth/register - create an account (L01: email + magic-link verification).
 *
 * `email` is REQUIRED (L01 FR5): the backend stores it lower-cased/trimmed,
 * emails a verification link, and gates login until it is confirmed. `email` is
 * format-validated client-side before this is called.
 *
 * OMEGA-762 (2026-08-17) — no payment token is sent. This used to forward an
 * opaque Stripe token and the backend used to 422 without one. The platform will
 * not collect payment from a stored card (decision by Rick Zhou; Omega issues its
 * own invoices, OMEGA-734 D1). This named bank transfer as the settlement rail
 * until OMEGA-773; that was rejected by the owner on 2026-08-18. No rail is named
 * here now, because E20 owns that decision and this client does not.
 *
 * OMEGA-772 — this said "`require_payment_on_register` now defaults false, so the
 * 422 is unreachable". That was the OMEGA-762 state and it went stale one commit
 * later: OMEGA-765 **retired the flag outright** and removed `payment_token` from
 * `RegisterRequest`. The 422 is not unreachable-by-default, it does not exist.
 *
 * Handles BOTH 201 response shapes (L01 FR7):
 *   - verification-pending `{ status: "verification_pending", username, email }`
 *     (`username` is the normalized email under OMEGA-814)
 *     when require_email_verification is on (deployed default) — NO JWT, so no
 *     token is stored; the caller shows a "check your inbox" state.
 *   - TokenResponse `{ access_token, token_type, username }` when the flag is
 *     off (local/dev auto-verify) — the JWT is stored exactly as before L01. It is
 *     a registration session (E23.6), so the Console shows the card step next.
 *
 * Errors arrive on the NEW envelope `{ code, message, request_id, details }`
 * (an already-registered-email 409, a 503, and the
 * password-complexity 422). readDetail surfaces the human `message` — never
 * rendered as "[object Object]".
 */
export async function register(fullName, email, password, billingProfile = {}) {
  // OMEGA-814 — `full_name` is the customer's DISPLAY name and replaces the old
  // `username` field. It does not identify the account and is not unique; the email
  // does both. Snake-cased because the wire contract is the backend's, not this
  // module's camelCase convention.
  //
  // E20.4 (OMEGA-987) — `billingProfile` carries the customer's BILLING identity, which is a
  // different thing from `full_name` above: it is the entity an invoice is made out to. Optional
  // by decision D1, so an omitted field is simply not sent and the customer completes it later on
  // the Account screen. Only non-empty values are included, because the backend refuses a blank
  // string (it would read as set while being absent to the completeness rule) and treats an
  // explicit null as "clear", which is meaningless at creation.
  const body = { full_name: fullName, email, password };
  for (const key of ["legal_name", "billing_email", "billing_address_lines", "billing_country_code"]) {
    const value = billingProfile[key];
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && !value.trim()) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    body[key] = typeof value === "string" ? value.trim() : value;
  }
  const res = await fetch(`${BASE}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await parseJsonOrThrow(res);
  // Only the auto-verify (flag-off) shape carries a JWT; the verification-pending
  // shape has none, so do NOT store a token — the account is unusable until the
  // emailed magic link is confirmed via verifyEmail().
  if (data.access_token) setToken(data.access_token);
  return data;
}

/**
 * POST /auth/verify-email - confirm a magic-link token and auto-login (L01 FR8).
 *
 * `token` is the RAW high-entropy token read from the emailed link's
 * `?token=<raw>` query. On success the backend flips `email_verified=true`,
 * consumes the single-use token, and returns a TokenResponse — we store the JWT
 * so the user is signed in (auto-login on verify). That JWT is a registration
 * session (E23.6), so they land on the card step rather than the Console. Errors:
 * 400 "invalid_or_expired_token", 410 "token_already_used" (single-use replay).
 */
export async function verifyEmail(token) {
  const res = await fetch(`${BASE}/auth/verify-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  const data = await parseJsonOrThrow(res);
  setToken(data.access_token);
  return data;
}

/**
 * POST /auth/registration/payment-method-session — open a provider card-setup session for a
 * registration session, accepting the consent of `consentVersion` (E23.5 FR1, FR2).
 *
 * Returns `{ setup_session_id, checkout_url, payment_method_status, setup_session_status, ... }`.
 * Two shapes carry no redirect, and the caller must branch on them rather than on an error: a
 * tenant whose card is already `ready` gets a 200 with that status and no session id (E23.5 FR6),
 * and a REPLAYED `idempotencyKey` returns the stored session with `checkout_url: null`, which is
 * why every new attempt sends a new key. Errors: 422 `payment_consent_required` (with
 * `details.expected` when the version is stale), 409 `conflict`, 502
 * `payment_provider_unavailable`, 503.
 */
export async function createPaymentMethodSession({ consentVersion, idempotencyKey }) {
  return fetchJson(`${BASE}/auth/registration/payment-method-session`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({
      consent_accepted: true,
      consent_version: consentVersion,
      idempotency_key: idempotencyKey,
    }),
  });
}

/**
 * POST /auth/registration:complete — verify the card setup server-side and receive the full
 * session that replaces the registration token (E23.5 FR3).
 *
 * Thin transport, like `login`: the returned TokenResponse is NOT stored here. The card step
 * commits it, so a completion resolving after the customer signed out cannot bring the session
 * back. Repeating the completion of the session that made the tenant ready is idempotent.
 * Errors: 404 `not_found` (not this account's session), 422 `unprocessable` (expired, not
 * confirmed, or not verifiable), 409 `conflict` (a card is already on file from another
 * session), 502, 503.
 */
export async function completeRegistration(setupSessionId) {
  return fetchJson(`${BASE}/auth/registration:complete`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ setup_session_id: setupSessionId }),
  });
}

/**
 * POST /auth/resend-verification - re-issue and re-send the verification email
 * (L01 FR10). ALWAYS resolves 202 with `{ status: "accepted" }` regardless of whether
 * the account exists or awaits verification (no account enumeration), so the caller
 * shows the same neutral confirmation either way. No token is stored (this does not
 * authenticate).
 *
 * OMEGA-814 — keyed on the same `identifier` as login, because its only caller is the
 * login screen's post-403 "Resend verification email" affordance and it forwards
 * whatever is in the login field. That field now holds an email. The always-202
 * contract is precisely what makes a key mismatch here dangerous: it would report
 * success, send nothing, and leave the customer watching an empty inbox.
 */
export async function resendVerification(identifier) {
  const res = await fetch(`${BASE}/auth/resend-verification`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identifier }),
  });
  return parseJsonOrThrow(res);
}

/**
 * POST /auth/forgot-password - request a password reset link (F14 FR3/FR4).
 *
 * Keyed on `email` (the reset link goes to a mailbox, so the mailbox is the
 * identifier). Under OMEGA-814 that is now the same value login is keyed on, which
 * is why the recovery flow no longer needs its own explanation of why it differs.
 *
 * ALWAYS resolves 202 with the byte-identical `{ status: "accepted" }` for an
 * eligible account, an unknown address, an inactive or unverified account, one
 * inside the per-account cooldown, and a legacy account with no email on file.
 * The caller therefore CANNOT branch on the outcome, and must not try: the
 * single neutral confirmation is the anti-enumeration contract, not a UI
 * convenience. Non-2xx here means transport or throttling (429 / 503), never
 * account state.
 *
 * Sends no Authorization header: this is a logged-OUT flow and a stale bearer
 * would be both useless and an unnecessary credential on the wire.
 */
export async function forgotPassword(email) {
  const res = await fetch(`${BASE}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return parseJsonOrThrow(res);
}

/**
 * POST /auth/reset-password - redeem a reset token and set a new password
 * (F14 FR9/FR10).
 *
 * `token` is the RAW token read from the emailed link's `?token=<raw>` query;
 * it is passed through from component state and is never written to
 * localStorage. Returns 204 with NO body, so there is nothing to parse, and
 * deliberately returns NO JWT: a reset does not sign you in (F14 FR10). The
 * caller sends the user back to login to authenticate with the new password.
 *
 * On success the stored JWT is cleared HERE as well as by the caller's session
 * reset. That is not belt-and-braces for its own sake: the reset screen renders
 * outside the auth gate, so a stale token left in localStorage would make
 * `hasToken()` true and let useAuth initialise a "logged in" session on the next
 * mount against a credential the server has just revoked.
 *
 * Errors: 400 "invalid_or_expired_token", 410 "token_already_used" (single-use
 * replay), 422 (password policy, surfaced by readDetail), 429, 503. None of them
 * is a 401, so none can trip the session-expiry auto-logout.
 */
export async function resetPassword(token, newPassword) {
  await fetchNoContent(`${BASE}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  clearToken();
  return undefined;
}


/**
 * GET /users/me — the authenticated caller's profile + tenant context (E05.2,
 * F07 FR1). Shape: { user_id, username, full_name, email, role, created_at,
 * onboarded_at, tenant: { tenant_id, name, plan, status } | null }.
 * OMEGA-814 — `full_name` is what the Console DISPLAYS and `email` is the login
 * identity it shows on the Account screen; both are null for an account created
 * before migration 091, which has neither on file. For a newer account `username`
 * duplicates `email`, which is how the identity is stored rather than a licence to
 * read one for the other. `role` is the literal
 * "customer" (no role column yet); `tenant` is null for legacy users without a
 * tenant_id. `onboarded_at` (F12 FR1) is the ISO first-login completion stamp,
 * or null until the customer completes/skips onboarding — the Console gates the
 * first-run experience on it. The profile object passes through unchanged, so
 * the new field is surfaced without further parsing. Errors: 404 "user not
 * found", 503 degraded (string details surfaced via the thrown Error message),
 * 401 on a bad token.
 */
export async function getProfile() {
  return fetchJson(`${BASE}/users/me`, { headers: headers() });
}

/**
 * POST /users/me/onboarding:complete — mark the caller's first-login onboarding
 * complete (F12 FR1). Bearer auth, NO request body. Idempotent: the first call
 * stamps `onboarded_at = now()`; a subsequent call is a no-op returning the same
 * value. Returns { onboarded_at: "<iso>" }. The `:complete` colon-verb sits
 * outside the path (matching `:cancel` / `inputs:confirm`). Called on the
 * activation milestone (first job -> first decision) or an explicit skip.
 * Errors: 401 on a bad token, 404 "user not found", 503 degraded (string
 * details surfaced via the thrown Error message).
 */
export async function completeOnboarding() {
  return fetchJson(`${BASE}/users/me/onboarding:complete`, {
    method: "POST",
    headers: headers(),
  });
}

/**
 * POST /auth/password — change the authenticated caller's password (E05.1,
 * F07 FR2). Body is ONLY { current_password, new_password } — the confirmation
 * is a client-only guard and is NEVER sent. The username comes from the JWT sub
 * claim, never the body. Returns 204 with NO body, so we resolve on res.ok
 * WITHOUT calling res.json() (an empty body would throw). Errors: 401 "current
 * password is incorrect" (NOT a session expiry — its code is absent from
 * SESSION_EXPIRED_CODES, so it never logs the user out), 422 (new_password 8-128
 * chars; the envelope's human `message` surfaced by readDetail), 503. Neither
 * password is logged.
 *
 * F14 — a SUCCESSFUL change now ends the session it was made from. The backend
 * increments `users.credential_version` in the same transaction as the hash, so
 * every JWT minted before it (this one included) is revoked at once; on success
 * we therefore clear the stored token and fire PASSWORD_CHANGED_EVENT. This
 * supersedes the E05.1 sentence "existing JWTs remain valid after a change",
 * which was true when written and is now false.
 */
export async function changePassword(currentPassword, newPassword) {
  // 204 No Content — there is no body to parse.
  await fetchNoContent(`${BASE}/auth/password`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  // F14 FR11/FR12 — the change bumped users.credential_version server-side, so
  // the JWT that authorised THIS request is already dead. Clear it and announce
  // the revocation rather than waiting for the next authenticated call to come
  // back 401: that would leave the user sitting in a Console whose every request
  // is about to fail, which reads as a broken app rather than a deliberate
  // sign-out. Only fires on success — a 401 (wrong current password) throws
  // above and must leave the working session completely untouched.
  signalPasswordChanged();
  return undefined;
}

/**
 * GET /tenants/me/api-keys — the authenticated tenant's API keys (E05.3,
 * F07 FR3). Shape: { keys: [ { key_id, name, key_prefix, created_at,
 * last_used_at|null, revoked_at|null } ] }. Server-sorted newest-first — DO NOT
 * re-sort. Never carries a secret or hash; revoked keys stay listed (FR4).
 * Errors: 400 without a tenant claim (object detail; readDetail surfaces its
 * message), 503 degraded, 401 on a bad token.
 */
export async function listApiKeys() {
  return fetchJson(`${BASE}/tenants/me/api-keys`, { headers: headers() });
}

/**
 * POST /tenants/me/api-keys — create a new API key (E05.3, F07 FR3). Body is
 * { name }. Returns 201 with the key metadata PLUS `secret`
 * (omega_<prefix>_<tail>) which is returned EXACTLY ONCE and never retrievable
 * again — the caller must surface it once and then discard it (FR4: never
 * persist/log/re-display). Errors: 422 (name empty/>64), 400 without a tenant
 * claim, 503 degraded.
 */
export async function createApiKey(name) {
  return fetchJson(`${BASE}/tenants/me/api-keys`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ name }),
  });
}

/**
 * DELETE /tenants/me/api-keys/{key_id} — revoke a key (E05.3, F07 FR3). Returns
 * 204 with NO body, so we resolve on res.ok WITHOUT calling res.json(). The
 * key's row is kept for audit (revoked_at set) and still appears in the list.
 * An unknown OR cross-tenant key_id both return 404 (by design — never 403, to
 * avoid confirming another tenant's key exists). Errors: 404, 400, 503.
 */
export async function revokeApiKey(keyId) {
  // 204 No Content — there is no body to parse.
  return fetchNoContent(`${BASE}/tenants/me/api-keys/${encodeURIComponent(keyId)}`, {
    method: "DELETE",
    headers: headers(),
  });
}


/**
 * GET /account-closure?token=<raw> — what an emailed closure link is about (A13 FR10).
 *
 * A pure READ. It is safe to call repeatedly and deliberately so: the link sits in an inbox
 * that scanners, preloaders and chat unfurlers reach before the customer does, so the landing
 * view fetches this on mount and sends nothing until a button is pressed.
 *
 * Returns `{ state, expires_at, account_masked }`. The address is masked by the server; the
 * holder already knows their own, and this endpoint takes no session.
 *
 * Errors: 404 `not_found` for a token that is unknown OR expired — one code for both, so the
 * response cannot be used to learn which tokens exist — and 409 `already_resolved` once the
 * request has been answered. Neither is a 401, so neither can trip the session-expiry logout.
 */
export async function readClosureRequest(token) {
  return fetchJson(`${BASE}/account-closure?token=${encodeURIComponent(token)}`, {
    headers: headers(),
  });
}

/**
 * POST /account-closure:confirm — the customer closes their own account (A13 FR8).
 *
 * The token travels in the BODY, never the query string, so it stays out of browser history,
 * proxy logs and `Referer`. A POST and not a GET for the stronger reason: a link being FETCHED
 * must never be a closure being PERFORMED.
 *
 * Returns `{ state: "confirmed", users_locked }`. The stored JWT is cleared here as well as by
 * the caller, because this screen renders outside the auth gate and the account's
 * `credential_version` has just moved: a stale token left behind would make `hasToken()` true
 * and let the next mount initialise a session against a credential the server just revoked.
 * Same reasoning as `resetPassword` above, and the same failure it prevents.
 */
export async function confirmClosure(token) {
  const result = await fetchJson(`${BASE}/account-closure:confirm`, {
    method: "POST",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  clearToken();
  return result;
}

/**
 * POST /account-closure:cancel — the customer stops the closure (A13 FR7, US4).
 *
 * Terminal. Both emailed links carry the SAME token, so cancelling is what makes the confirm
 * link stop working; a customer who changes their mind is not left with a countdown they
 * cannot stop. The stored token is NOT cleared: nothing was revoked, and signing the customer
 * out of a session they still have would punish them for saying no.
 */
export async function cancelClosure(token) {
  return fetchJson(`${BASE}/account-closure:cancel`, {
    method: "POST",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
}
